import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import StarterSection, { StarterProgress } from '../components/starter/StarterSection';
import {
  PersonalFields, AddressFields, BankFields, StarterDocumentFields,
} from '../components/starter/StarterFields';
import {
  validatePersonal, validateAddress, validateBank, scrollToFirstError,
  firstSectionWithError, errorSummary, normaliseStarterFields, validateFiles,
} from '../utils/starterValidation';


/**
 * The new starter's own form, opened from the link their employer sent.
 *
 * There is no account behind this page — the token in the URL is the only
 * credential — so it holds nothing the recipient didn't already have: their
 * employer's name and the job they were offered. Not the salary; the employer
 * sets that and a mistyped figure is a conversation for the two of them.
 */

const initialForm = {
  first_name: '', last_name: '', email: '', gender: '', date_of_birth: '',
  address_line_1: '', address_line_2: '', city: '', postcode: '',
  ni_number: '',
  bank_account_name: '', sort_code: '', account_number: '', account_number_confirm: '',
  has_p45: 'no',
};

const SECTION_LABELS = ['Your details', 'Address', 'Bank details', 'P45'];
// Which fields live in which section, so a failed submit can move to the
// section holding the problem. A section that isn't active renders nothing, so
// an error in a collapsed one would otherwise have nowhere to show.
const SECTION_FIELDS = [
  ['first_name', 'last_name', 'email', 'gender', 'date_of_birth', 'ni_number'],
  ['address_line_1', 'address_line_2', 'city', 'postcode'],
  ['bank_account_name', 'sort_code', 'account_number', 'account_number_confirm'],
  ['documents'],
];
const SECTION_DESCS = [
  'As they appear on official documents',
  'Where you live',
  'Where your pay lands',
  'P45 or HMRC starter checklist',
];

function formatDate(value) {
  if (!value) return '';
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  const d = iso ? new Date(Date.UTC(+iso[1], +iso[2] - 1, +iso[3])) : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function PageShell({ children }) {
  const { darkMode } = useTheme();
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="bg-brand-600 py-6 px-4 text-center">
        <img src={darkMode ? '/logo-white - full.png' : '/Logo in black.png'}
          alt="Startup Accountancy" className="h-12 mx-auto" />
        <p className="text-brand-100 text-sm mt-2">Payroll Portal</p>
      </div>
      {children}
    </div>
  );
}

export default function StarterFormPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [uploadFiles, setUploadFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // The summary appears only once they have tried to send — nagging someone
  // about an empty field they have not reached yet is just noise.
  const [triedSubmit, setTriedSubmit] = useState(false);
  const [done, setDone] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (!token) {
      setLoadError('This link is missing its code. Ask your employer to send you a new one.');
      setLoading(false);
      return;
    }
    api.get(`/starter-form/${encodeURIComponent(token)}`)
      .then(res => {
        setInvite(res.data);
        setForm(prev => ({
          ...prev,
          first_name: res.data.first_name || '',
          last_name: res.data.last_name || '',
          email: res.data.email || '',
        }));
        setLoading(false);
      })
      .catch(err => {
        setLoadError(err.response?.data?.error || 'We could not open this form. Please try the link again.');
        setLoading(false);
      });
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleFileChange = (e) => {
    const chosen = Array.from(e.target.files);
    // Caught here so an oversized photo is rejected the moment it is picked,
    // not after the whole form has been filled in and sent.
    const fileError = validateFiles(chosen);
    if (fileError) {
      setUploadFiles([]);
      setErrors(prev => ({ ...prev, documents: fileError }));
      e.target.value = '';
      return;
    }
    setUploadFiles(chosen);
    setErrors(prev => ({ ...prev, documents: null }));
  };

  const inputClass = (field) =>
    `mt-1 block w-full rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 ${
      errors[field] ? 'border-red-300' : 'border-gray-300'
    }`;

  // Everything wrong with the form as it stands. The progress strip and the
  // submit check both read this, so a section can never show complete while
  // holding something that would be rejected.
  const allErrors = useMemo(() => {
    const errs = {
      ...validatePersonal(form, { selfService: true }),
      ...validateAddress(form),
      ...validateBank(form, { confirmAccountNumber: true }),
    };
    if (uploadFiles.length === 0) {
      errs.documents = form.has_p45 === 'yes'
        ? 'Please attach your P45'
        : 'Please attach your completed HMRC starter checklist';
    }
    return errs;
  }, [form, uploadFiles]);

  const sectionDone = useMemo(
    () => SECTION_FIELDS.map(fields => fields.every(field => !allErrors[field])),
    [allErrors]
  );

  const summary = useMemo(() => errorSummary(allErrors), [allErrors]);
  const showSummary = triedSubmit;

  const validate = () => {
    setTriedSubmit(true);
    setErrors(allErrors);
    const failed = firstSectionWithError(allErrors, SECTION_FIELDS);
    if (failed === null) return true;
    // Move to the section that needs attention before scrolling — otherwise the
    // field being pointed at isn't on the page.
    setActiveStep(failed);
    scrollToFirstError();
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!validate()) return;

    setSubmitting(true);
    try {
      // The document goes first: there is an invite to attach it to from the
      // outset, so if the upload fails nothing has been submitted yet.
      const formData = new FormData();
      for (const file of uploadFiles) formData.append('documents', file);
      formData.append('document_type', form.has_p45 === 'yes' ? 'p45' : 'starter_checklist');
      await api.post(`/starter-form/${encodeURIComponent(token)}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const { account_number_confirm, has_p45, ...details } = normaliseStarterFields(form);
      await api.post(`/starter-form/${encodeURIComponent(token)}`, details);
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'Something went wrong. Please try again.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-gray-500">Opening your form...</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <PageShell>
        <div className="max-w-md mx-auto px-4 py-10">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-8 text-center">
            <h2 className="font-heading text-[20px] font-bold text-ink dark:text-gray-100 mb-2">This link isn't working</h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm">{loadError}</p>
          </div>
        </div>
      </PageShell>
    );
  }

  if (done || invite?.already_completed) {
    return (
      <PageShell>
        <div className="max-w-md mx-auto px-4 py-10">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-100 grid place-items-center mx-auto mb-4">
              <svg className="w-6 h-6 text-emerald-700" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2 className="font-heading text-[20px] font-bold text-ink dark:text-gray-100 mb-2">
              {done ? "That's everything, thank you" : 'We already have your details'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Your details are with {invite?.company_name} and you're in their next payroll run.
              There's nothing else for you to do.
            </p>
            <p className="text-gray-500 dark:text-gray-500 text-[13px] mt-4">
              This form is now closed. If something you sent was wrong, tell {invite?.company_name} and
              they can send you a fresh one.
            </p>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="max-w-[720px] mx-auto px-5 py-7">
        <header className="mb-4">
          <p className="text-[11.5px] uppercase tracking-[0.14em] font-bold text-brand-700">New starter details</p>
          <h1 className="font-heading text-[26px] leading-tight text-ink dark:text-gray-100 mt-1">
            Let's get you set up, {invite?.first_name}
          </h1>
          <p className="text-[14px] text-gray-500 dark:text-gray-400 mt-1.5">
            {invite?.company_name} asked us to collect these details so you're paid correctly from your
            first payday. It takes about five minutes.
          </p>
        </header>

        {submitError && (
          <div className="mb-4 p-3 bg-rose-50 border border-rose-200 rounded text-sm text-rose-700">
            {submitError}
          </div>
        )}

        {showSummary && summary.length > 0 && (
          <div className="mb-4 p-3 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded">
            <p className="text-sm font-semibold text-rose-800 dark:text-rose-200">
              {summary.length === 1 ? 'One thing to fix before you send' : `${summary.length} things to fix before you send`}
            </p>
            <ul className="mt-1.5 space-y-0.5">
              {summary.map(item => (
                <li key={item.field} className="text-[13px] text-rose-700 dark:text-rose-300">
                  <button type="button"
                    onClick={() => {
                      const target = firstSectionWithError({ [item.field]: item.message }, SECTION_FIELDS);
                      if (target !== null) setActiveStep(target);
                    }}
                    className="underline underline-offset-2 hover:no-underline text-left">
                    {item.label}
                  </button>
                  {' — '}{item.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* What the employer told us. Read-only — it is theirs to change, and
            showing it is what lets the recipient tell a real form from a scam. */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl ring-1 ring-gray-200 dark:ring-gray-700 overflow-hidden mb-4">
          <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3">
            <h2 className="font-heading text-[14px] text-ink dark:text-gray-100">
              Your job, as {invite?.company_name} gave it to us
            </h2>
            <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11.5px] font-semibold bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 ring-1 ring-gray-200 dark:ring-gray-600">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                <rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V7a4 4 0 018 0v4" />
              </svg>
              Read only
            </span>
          </div>
          <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.1em] font-bold text-gray-500">Employer</p>
              <p className="text-[14px] font-semibold text-ink dark:text-gray-100 mt-1">{invite?.company_name}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.1em] font-bold text-gray-500">Job title</p>
              <p className="text-[14px] font-semibold text-ink dark:text-gray-100 mt-1">{invite?.job_title || 'Not given'}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.1em] font-bold text-gray-500">Start date</p>
              <p className="text-[14px] font-semibold text-ink dark:text-gray-100 mt-1 tabular">{formatDate(invite?.start_date) || 'Not given'}</p>
            </div>
          </div>
          <div className="px-5 py-2.5 bg-gray-50/60 dark:bg-gray-900/30 border-t border-gray-100 dark:border-gray-700">
            <p className="text-[12px] text-gray-500 dark:text-gray-400">
              Something wrong here? Speak to {invite?.company_name} — we can't change it from this page.
            </p>
          </div>
        </div>

        <StarterProgress
          labels={SECTION_LABELS}
          done={sectionDone}
          activeStep={activeStep}
          onJump={setActiveStep}
          hint="Tap any section to jump"
        />

        <form onSubmit={handleSubmit} className="space-y-8 mt-4">
          <StarterSection step={0} total={SECTION_LABELS.length} label={SECTION_LABELS[0]} desc={SECTION_DESCS[0]}
            active={activeStep === 0} onPrev={() => setActiveStep(s => s - 1)} onNext={() => setActiveStep(s => s + 1)}>
            <PersonalFields form={form} errors={errors} onChange={handleChange} inputClass={inputClass} selfService />
            <div className="mt-5 rounded-md bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 px-3 py-2.5">
              <p className="text-[12.5px] text-gray-600 dark:text-gray-400 leading-relaxed">
                <span className="font-semibold text-gray-700 dark:text-gray-300">Your data.</span> Startup
                Accountancy processes these details to run {invite?.company_name}'s payroll and report to HMRC.
                Your National Insurance number and bank details are encrypted, and are shared only with
                {' '}{invite?.company_name} for the purpose of paying you.
              </p>
            </div>
          </StarterSection>

          <StarterSection step={1} total={SECTION_LABELS.length} label={SECTION_LABELS[1]} desc={SECTION_DESCS[1]}
            active={activeStep === 1} onPrev={() => setActiveStep(s => s - 1)} onNext={() => setActiveStep(s => s + 1)}>
            <AddressFields form={form} errors={errors} onChange={handleChange} inputClass={inputClass} />
          </StarterSection>

          <StarterSection step={2} total={SECTION_LABELS.length} label={SECTION_LABELS[2]} desc={SECTION_DESCS[2]}
            active={activeStep === 2} onPrev={() => setActiveStep(s => s - 1)} onNext={() => setActiveStep(s => s + 1)}>
            <BankFields form={form} errors={errors} onChange={handleChange} inputClass={inputClass} confirmAccountNumber />
          </StarterSection>

          <StarterSection step={3} total={SECTION_LABELS.length} label={SECTION_LABELS[3]} desc={SECTION_DESCS[3]}
            active={activeStep === 3} onPrev={() => setActiveStep(s => s - 1)} onNext={() => setActiveStep(s => s + 1)}>
            <StarterDocumentFields
              form={form}
              errors={errors}
              files={uploadFiles}
              onToggleP45={(val) => setForm(prev => ({ ...prev, has_p45: val }))}
              onFileChange={handleFileChange}
              selfService
            />
          </StarterSection>

          <div className="flex flex-wrap items-center justify-end gap-3">
            <button type="submit" disabled={submitting}
              className="px-5 py-2 text-[13px] font-semibold text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50">
              {submitting ? 'Sending…' : 'Send my details →'}
            </button>
          </div>
        </form>
      </div>
    </PageShell>
  );
}
